import React from 'react';
import { Table, Button, Descriptions, Tag } from 'antd';
import { useNavigate } from 'react-router-dom';

const FIELD_LABELS: Record<string, string> = {
  customer_name: 'Customer',
  customer_id: 'Customer ID',
  balance: 'Balance',
  overdue_amount: 'Overdue',
  amount: 'Amount',
  days_overdue: 'Days overdue',
  invoice_number: 'Invoice',
  po_number: 'PO number',
  supplier_name: 'Supplier',
  work_order_number: 'Work order',
  delivery_note_number: 'Delivery note',
  count: 'Count',
  metric: 'Metric',
  area: 'Area',
  severity: 'Severity',
  title: 'Title',
  description: 'Description',
};

function humanLabel(key: string): string {
  return FIELD_LABELS[key] ?? key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

export function OiExecutiveTablePanel({
  rows,
  erpLink,
  erpLinkLabel = 'Open in ERP',
}: {
  rows: Record<string, unknown>[];
  erpLink?: string;
  erpLinkLabel?: string;
}) {
  const navigate = useNavigate();
  if (!rows?.length) return <p className="oi-text-muted">No detail rows available.</p>;
  const keys = Object.keys(rows[0]).filter((k) => !k.startsWith('_') && k !== 'id');
  const columns = keys.slice(0, 6).map((k) => ({
    title: humanLabel(k),
    dataIndex: k,
    render: (v: unknown) => (typeof v === 'object' ? JSON.stringify(v) : String(v ?? '—')),
  }));
  return (
    <>
      <Table size="small" rowKey={(_, i) => String(i)} pagination={{ pageSize: 8 }} dataSource={rows} columns={columns} />
      {erpLink ? (
        <Button type="link" style={{ paddingLeft: 0, marginTop: 8 }} onClick={() => navigate(erpLink)}>
          {erpLinkLabel}
        </Button>
      ) : null}
    </>
  );
}

export function OiKpiDrillDownPanel({
  kpiCode,
  rows,
}: {
  kpiCode: string;
  rows: unknown[];
}) {
  const navigate = useNavigate();
  const list = (rows ?? []) as Record<string, unknown>[];
  const erpRoutes: Record<string, string> = {
    overdue_ar_count: '/fin/reports/customer-outstanding',
    ar_outstanding: '/fin/reports/customer-outstanding',
    delayed_deliveries: '/sales/delivery-notes',
    delayed_work_orders: '/manufacturing/work-orders',
    production_at_risk: '/manufacturing/work-orders',
    delivered_not_invoiced: '/sales/invoices',
    overdue_pos: '/procurement/purchase-orders',
  };
  return (
    <OiExecutiveTablePanel
      rows={list}
      erpLink={erpRoutes[kpiCode]}
      erpLinkLabel="Open related records"
    />
  );
}

export function OiCashChasePanel({ row }: { row: Record<string, unknown> }) {
  const navigate = useNavigate();
  return (
    <Descriptions column={1} size="small" bordered>
      <Descriptions.Item label="Customer">{String(row.customer_name ?? row.name ?? '—')}</Descriptions.Item>
      <Descriptions.Item label="Overdue">{String(row.overdue_amount ?? row.balance ?? row.amount ?? '—')}</Descriptions.Item>
      <Descriptions.Item label="Payment behavior">{String(row.payment_behavior ?? row.avg_days_overdue ?? '—')}</Descriptions.Item>
      <Descriptions.Item label="Health score">{row.health_score != null ? `${row.health_score}%` : '—'}</Descriptions.Item>
    </Descriptions>
  );
}

export function OiRiskRowPanel({ row }: { row: Record<string, unknown> }) {
  return (
    <Descriptions column={1} size="small" bordered>
      <Descriptions.Item label="Area">{String(row.area ?? '—')}</Descriptions.Item>
      <Descriptions.Item label="Risk">{String(row.title ?? row.metric ?? '—')}</Descriptions.Item>
      <Descriptions.Item label="Severity">
        <Tag color={row.severity === 'critical' ? 'red' : row.severity === 'warning' ? 'orange' : 'blue'}>
          {String(row.severity ?? '—')}
        </Tag>
      </Descriptions.Item>
      <Descriptions.Item label="Count">{String(row.count ?? row.actual_value ?? '—')}</Descriptions.Item>
      <Descriptions.Item label="Evidence">{String(row.linked_evidence ?? row.description ?? '—')}</Descriptions.Item>
    </Descriptions>
  );
}

export function OiSupplierPanel({ row }: { row: Record<string, unknown> }) {
  const navigate = useNavigate();
  return (
    <>
      <Descriptions column={1} size="small" bordered>
        <Descriptions.Item label="Supplier">{String(row.supplier_name ?? '—')}</Descriptions.Item>
        <Descriptions.Item label="Overdue POs">{String(row.overdue_count ?? '—')}</Descriptions.Item>
        <Descriptions.Item label="Open value">{String(row.open_value ?? row.total_value ?? '—')}</Descriptions.Item>
      </Descriptions>
      <Button type="link" style={{ paddingLeft: 0, marginTop: 8 }} onClick={() => navigate('/procurement/purchase-orders')}>
        View purchase orders
      </Button>
    </>
  );
}

/** @deprecated Prefer specific panels above */
export function OiDetailJsonPanel({ data }: { data: Record<string, unknown> | unknown }) {
  if (!data || typeof data !== 'object') return <p className="oi-text-muted">No detail available.</p>;
  if (Array.isArray((data as { rows?: unknown }).rows)) {
    return (
      <OiExecutiveTablePanel rows={(data as { rows: Record<string, unknown>[] }).rows} />
    );
  }
  const entries = Object.entries(data as Record<string, unknown>);
  if (!entries.length) return <p className="oi-text-muted">No detail available.</p>;
  return (
    <Descriptions column={1} size="small" bordered>
      {entries.map(([k, v]) => (
        <Descriptions.Item key={k} label={humanLabel(k)}>
          {typeof v === 'object' ? JSON.stringify(v) : String(v ?? '—')}
        </Descriptions.Item>
      ))}
    </Descriptions>
  );
}
